/**
 * Services API
 * Framework-agnostic API client for reference data (employees, projects, customers, activities)
 */

import { formatAPIDate } from "@/utils/date";
import type {
  IServiceActivity,
  IServiceCustomer,
  IServiceEmployee,
  IServiceProject,
} from "./types";

// API base URL - will be configured via environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

// Token getter function - set by auth system
let getAuthToken: (() => Promise<string | null>) | null = null;

export function setAuthTokenGetter(getter: () => Promise<string | null>): void {
  getAuthToken = getter;
}

async function fetchWithAuth(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (getAuthToken) {
    const token = await getAuthToken();
    if (token) {
      (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return response;
}

export class ServicesAPI {
  async getProjects(): Promise<IServiceProject[]> {
    const response = await fetchWithAuth("/projects?timesheetentry=1");
    return response.json();
  }

  async getEmployees(): Promise<IServiceEmployee[]> {
    const response = await fetchWithAuth("/employees");
    return response.json();
  }

  async getProjectEmployees(
    projects: IServiceProject[],
    fromDate?: Date,
    toDate?: Date,
  ): Promise<IServiceEmployee[]> {
    const projectIds = projects.map((p) => p.id).join(",");
    let path = `/projects/employees?project=${projectIds}`;

    if (fromDate) {
      path += `&from_date=${formatAPIDate(fromDate)}`;
    }
    if (toDate) {
      path += `&to_date=${formatAPIDate(toDate)}`;
    }

    const response = await fetchWithAuth(path);
    return response.json();
  }

  async getActivities(): Promise<IServiceActivity[]> {
    const response = await fetchWithAuth("/activities");
    const data: IServiceActivity[] = await response.json();
    // Filter out activities without code
    return data.filter((a) => a.code != null);
  }

  async getCustomers(): Promise<IServiceCustomer[]> {
    const response = await fetchWithAuth("/customers");
    return response.json();
  }

  async createCustomer(customerName: string): Promise<IServiceCustomer[]> {
    const response = await fetchWithAuth("/customers", {
      method: "PUT",
      body: JSON.stringify({ name: customerName }),
    });

    if (response.status !== 201) {
      throw new Error(`Failed to create customer: ${response.status}`);
    }

    return response.json();
  }

  async deleteCustomer(id: number): Promise<IServiceCustomer[]> {
    const response = await fetchWithAuth(`/customers/${id}`, {
      method: "DELETE",
    });

    if (response.status !== 200) {
      throw new Error(`Failed to delete customer: ${response.status}`);
    }

    return response.json();
  }
}

// Singleton instance
export const servicesAPI = new ServicesAPI();
